import { z } from 'zod'

/** Request body for bulk asset deletion (`POST /:tenantId/assets/bulk-delete`). */
export const bulkDeleteAssetsRequestSchema = z.object({
  urns: z.array(z.string().min(1)).min(1).max(1000)
})
export type BulkDeleteAssetsRequest = z.infer<typeof bulkDeleteAssetsRequestSchema>

/** Why an asset was not deleted during a bulk delete (business rule, not a transient failure). */
export const BULK_DELETE_ASSET_SKIP_REASONS = ['in_use_by_playlist', 'used_by_outdated_campaign'] as const
export type BulkDeleteAssetSkipReason = (typeof BULK_DELETE_ASSET_SKIP_REASONS)[number]

/** Per-URN outcome of a bulk asset deletion. */
export interface BulkDeleteAssetsResult {
  /** URNs that were fully deleted (rows + R2 blobs). */
  deleted: string[]
  /** URNs intentionally left in place (e.g. still referenced by a playlist or outdated campaign). */
  skipped: Array<{ urn: string, reason: BulkDeleteAssetSkipReason }>
  /** URNs that hit an unexpected error while deleting. */
  failed: Array<{ urn: string, message: string }>
}

export interface ICachedAsset {
  urn: string
  size: number
  type: string
  lastAccess: Date
  /**
   * Campaign bytes in IndexedDB. Omitted on Android when {@link nativeFilePath} holds the
   * progressive MP4 for Media3 (P2 file cache — IDB blob off the Exo hot path).
   */
  blob?: Blob
  etag: string
  /** Asset `updatedAt` (ms) when this blob was stored; used by the app gallery to skip re-downloading thumbnails across sessions. */
  sourceUpdatedAtMs?: number
  /** Absolute filesystem path under app `files/hybrid-cache/` (Android Media3). */
  nativeFilePath?: string
}

/** True when playback bytes live on disk, not in an IndexedDB Blob. */
export function isFileBackedCachedAsset(asset: ICachedAsset | null | undefined): boolean {
  return typeof asset?.nativeFilePath === 'string' && asset.nativeFilePath.trim() !== ''
}

/**
 * IDB row for Media3: keep metadata + {@link nativeFilePath}, drop the Blob.
 * `idb.put` replaces the record, so omitting `blob` frees IndexedDB bytes.
 * Coerces a non-video `type` (e.g. leftover `application/octet-stream`) to `video/mp4`
 * so VBuffer still treats the row as file-backed video after the blob is gone.
 */
export function cachedAssetWithoutIdbBlob(asset: ICachedAsset, nativeFilePath: string): ICachedAsset {
  const { blob: _omit, ...rest } = asset
  const type = rest.type.toLowerCase().includes('video') ? rest.type : 'video/mp4'
  return { ...rest, type, nativeFilePath }
}

/** Metadata shape for HLS video assets (manifest URL, optional poster). Aligns API, app and player. */
export interface IHlsVideoAssetMetadata {
  hlsUrl: string
  posterUrl?: string
}
